import { Component, inject, Input, OnInit } from '@angular/core';
import { NZ_MODAL_DATA, NzModalRef } from 'ng-zorro-antd/modal';
import { Roles, ROLES_MESSAGE_MAP } from '../../../../core/constants/roles.enum';
import { KYC_STATUS_MAP, KYC_STATUS_NUMBER_MAP, KycStatus } from '../../../../core/constants/kyc-status.enum';
import { Attachment, DocumentType } from '../../../models/attachment.model';
import { AttachmentPreviewManager } from '../../../managers/attachment-preview.manager';
import { IndividualDocumentType, StateIndividual } from '../../../models/individual.model';
import { SharedService } from '../../../services/shared.service';
import { AttachmentService } from '../../../services/attachment.service';
import { MessageHelper } from '../../../../core/helpers/message-helper';
import { AttachmentDownloader } from '../../../../core/helpers/attachment-downloader';
import { RegEntityDocumentPreviewManager } from '../../../managers/document-preview.manager';

@Component({
    selector: 'app-individual-modal',
    templateUrl: './individual-modal.component.html',
    styleUrls: ['./individual-modal.component.scss'],
    standalone: false
})
export class IndividualModalComponent implements OnInit {

  //@ts-ignore
  @Input() individual: StateIndividual;
  @Input() isPayer: boolean = false;
  @Input() isRecipient: boolean = false;
  @Input() isEntity: boolean = false;

  isVisible: boolean = true;
  excludedRoles: Roles[] = [Roles.PAYR, Roles.PCNT, Roles.RCPT];
  KycStatus = KycStatus;

  kycReport: Attachment | null = null;
  kycIdentityDocs: Attachment[] | null = null;

  qualifiedRecipientLines = [
    "a Person holding a Depository Receipt;",
    "a Person who is a prospective recipient of a fixed, or discretionary, share of the property of the Foundation upon the happening of a future event specified in the By-laws;",
    "a Person who is nominated pursuant to the By-laws to be a recipient of a fixed, or discretionary, share of the property and income of the Foundation at a time following the establishment of the Foundation;",
    "a Person who is nominated pursuant to the By-laws to be a recipient of a fixed, or discretionary, share of the property and income of the Foundation at a time following the establishment of the Foundation;",
    "a charity;",
    "a default recipient"
  ];
  public readonly nzModalData: any = inject(NZ_MODAL_DATA);

  constructor(public modal: NzModalRef<IndividualModalComponent>, 
    private attachmentService: AttachmentService,
    private shared: SharedService,
    private attachmentPreviewManager: AttachmentPreviewManager,
    private documentPreviewManager: RegEntityDocumentPreviewManager
  ) {

  }

  ngOnInit(): void {
    this.individual = this.nzModalData.individual;
    this.isPayer = this.nzModalData.isPayer;
    this.isRecipient = this.nzModalData.isRecipient;
    this.isEntity = this.nzModalData.isEntity;

    this.kycReport = this.getKycReport(this.individual.kycData?.reportDocumentId!);
    this.kycIdentityDocs = this.getKycIdentityDocs(this.individual.kycData?.identityDocumentIds!);
  }

  private getKycReport(id: number): Attachment | null {
    let result: Attachment | null = null;

    // Registered Entity
    if (this.isEntity && this.attachmentService.documents.length > 0) {
      let doc = this.attachmentService.documents.find(i => i.id == id);
      if (doc != null) {
        result = {
            fileName: doc.fileName,
            createdAt: doc.createdAt,
            documentId: doc.id,
            fileKey: doc.fileKey
        } as Attachment;
      }
    }
    //Registration
    else if (!this.isEntity && this.attachmentService.attachments.length > 0) {
      result = this.attachmentService.attachments.find(i => i.documentType == DocumentType.KYC_REPORT && i.documentId == id) || null;
    }

    return result;
  }

  private getKycIdentityDocs(ids: number[]): Attachment[] {
    let result: Attachment[] = [];

    // Registered Entity
    if (this.isEntity && this.attachmentService.documents.length > 0) {
      let docs = this.attachmentService.documents.filter(i => i.code == DocumentType.KYC_IDENTITY_DOC && ids?.includes(i.id)) || []
      if (docs.length > 0) {
        result = docs.map(doc => ({
          fileName: doc.fileName,
          createdAt: doc.createdAt,
          documentId: doc.id,
          fileKey: doc.fileKey
        } as Attachment));
      }
    }
    //Registration
    else if (!this.isEntity && this.attachmentService.attachments.length > 0) {
      result = this.attachmentService.attachments.filter(i => i.documentType == DocumentType.KYC_IDENTITY_DOC && ids?.includes(i.documentId)) || [];
    }


    return result;
  }

  mapRoleToText(role: string): string {
    return ROLES_MESSAGE_MAP.get(role) || role;
  }

  getFilteredJobTitlesIndividual(individual: StateIndividual): Roles[] {
    return individual.jobTitles.map(role => role.titleCode as Roles).filter(role => !this.excludedRoles.includes(role));
  }

  get getPhoneNumberPrefix(): string | null  {
    let country = this.shared.countries.find(c => c.isoCode3 == this.individual.phoneCountryCode);
    if (country) {
      return country.phonePrefix;
    }
    return null;
  }

  getKycStatus(status: string): string {
    return KYC_STATUS_MAP.get(status as KycStatus) || KYC_STATUS_NUMBER_MAP.get(status) || status.toString();
  }

  previewFile(att: Attachment) {
    if (this.isEntity) {
      this.documentPreviewManager.openPreview({fileKey: att.fileKey, fileName: att.fileName});
      // this.downloadDocument(att);
    }
    else {
      this.attachmentPreviewManager.openPreview({ fileId: att.documentId, fileName: att.fileName, applicationId: att.backOfficeApplicationId});
    }
  }

  
  
  downloadDocument(att: Attachment) {
    this.attachmentService.downloadDocument(att.fileKey, att.fileName).subscribe({
      next: (blob: Blob) => {
        AttachmentDownloader.downloadFile(blob, att);
      },
      error: err => {
        console.error('Ошибка при скачивании файла:', err);
      }
    });
  };

  getDocumentText(item: string): string {
    return MessageHelper.getIndividualDocumentTypeText(item as IndividualDocumentType) || item;
  }

  isResident(): boolean {
    return this.individual.isResident == true;
  }

  isCeo(): boolean {
    return this.individual.jobTitles.some(i => i.titleCode == 'CEO');
  }


  getCountryName(id?: number | null): string {
    if (id == null) return '—';
    return this.shared.getCountryNameById(id) || String(id);
  }
}
